import { Input, Space } from 'antd';
import type { VFC } from 'react';
import { CustomersSelect, InputNumber } from 'components';
import { Form } from 'components/enmon/form/form_ant/Form';
import { ResellersSelect } from 'components/form/select/ResellersSelect';
import type { Rule } from 'antd/lib/form';
import {
  CreditEntity,
  Currency,
  CURRENCY_TRANSLATED,
  ImplementationEntity,
  type SerializedCreditInvoice,
} from '@enmon/commons/lib/enmon';
import type { PopulatedSerializedCreditInvoice } from 'lib/services';
import Title from 'antd/lib/typography/Title';
import { CurrencySelect } from 'components/form/select/CurrencySelect';
import { useWatch } from 'antd/lib/form/Form';
import { StyledDayjsDatePicker } from 'components/form/input/DayJSDatePicker';
import dayjs from 'dayjs';
import { useUpdateCreditInvoice } from '../hooks/useUpdateCreditInvoice';
import { useCreateCreditInvoice } from '../hooks/useCreateCreditInvoice';

export const RESELLERS_CREDIT_INVOICES_FORM_ID = 'reseller-credits';

const numberInputRules: Rule[] = [
  {
    min: 0,
    type: 'number',
    message: 'Please enter a valid number (min. 0).',
    required: true,
  },
];

export type CreditInvoiceFormValues = Omit<SerializedCreditInvoice, 'customer' | 'reseller'> & {
  customer: {
    _id: string;
  };
  reseller: {
    _id: string;
  };
};

export const CreditInvoiceForm: VFC<{
  creditInvoice: PopulatedSerializedCreditInvoice;
  onSubmitted: () => void;
}> = ({ creditInvoice, onSubmitted }) => {
  const [form] = Form.useForm<CreditInvoiceFormValues>();

  const { mutate: updateCreditInvoice } = useUpdateCreditInvoice();
  const { mutate: createCreditInvoice } = useCreateCreditInvoice();

  const reseller = useWatch('reseller', form);
  const currency = useWatch('currency', form);

  const handleSubmit = (values: CreditInvoiceFormValues) => {
    const simplifiedValues = {
      ...values,
      customer: values.customer._id,
      reseller: values.reseller._id,
    };

    if (creditInvoice) updateCreditInvoice({ _id: creditInvoice._id, data: simplifiedValues });
    else createCreditInvoice(simplifiedValues);

    onSubmitted();
  };

  return (
    <Form
      form={form}
      id={RESELLERS_CREDIT_INVOICES_FORM_ID}
      initialValues={{
        ...creditInvoice,
        date: creditInvoice?.date ? dayjs(creditInvoice.date) : undefined,
        currency: creditInvoice?.currency ?? Currency.CZK,
      }}
      labelAlign="left"
      labelCol={{ span: 12 }}
      layout="horizontal"
      name={RESELLERS_CREDIT_INVOICES_FORM_ID}
      onFinish={handleSubmit}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          form.submit();
        }
      }}
    >
      <Space className="w-100" direction="vertical" size={4} style={{ marginBottom: 24 }}>
        <Form.Item label="Reseller" name={['reseller', '_id']} rules={[{ min: 0, type: 'string', required: true }]}>
          <ResellersSelect placeholder="Select reseller" />
        </Form.Item>
        <Form.Item label="Customer" name={['customer', '_id']} rules={[{ min: 0, type: 'string', required: true }]}>
          <CustomersSelect filter={{ customer: reseller?._id, depth: -1 }} />
        </Form.Item>
        <Form.Item label="Invoice number" name="invoiceNumber" rules={[{ min: 0, type: 'string', required: true }]}>
          <Input placeholder="Enter invoice number" />
        </Form.Item>
        <Form.Item label="Issue date" name="date" rules={[{ min: 0, type: 'date', required: true }]}>
          <StyledDayjsDatePicker format="YYYY/MM/DD" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          initialValue={currency}
          label="Currency"
          name="currency"
          rules={[{ min: 0, type: 'string', required: true }]}
        >
          <CurrencySelect options={[Currency.CZK, Currency.EUR]} />
        </Form.Item>
      </Space>

      <Title level={5}>CapEx (Implementations)</Title>
      <Space direction="vertical" size={4} style={{ marginBottom: 24 }}>
        {[
          { name: ImplementationEntity.Customer, label: 'Customer' },
          { name: ImplementationEntity.Establishment, label: 'Establishment' },
          { name: ImplementationEntity.MeterOnline, label: 'Meter online' },
          { name: ImplementationEntity.MeterOffline, label: 'Meter offline' },
        ].map(({ name, label }) => (
          <Form.Item key={name} label={label} rules={[{ min: 0, type: 'string' }]}>
            <Space>
              <Form.Item noStyle initialValue={0} name={['implementation', name, 'count']} rules={numberInputRules}>
                <InputNumber controls={false} min={0} placeholder="50" style={{ width: 90 }} type="number" />
              </Form.Item>
              <Form.Item noStyle initialValue={0} name={['implementation', name, 'price']} rules={numberInputRules}>
                <InputNumber
                  addonAfter={CURRENCY_TRANSLATED[currency]}
                  controls={false}
                  min={0}
                  placeholder="Cost"
                  type="number"
                />
              </Form.Item>
            </Space>
          </Form.Item>
        ))}
      </Space>

      <Space direction="vertical" size={4}>
        <Title level={5}>OpEx (Credits)</Title>
        {[
          { name: CreditEntity.Establishment, label: 'Establishment' },
          { name: CreditEntity.MeterOnline, label: 'Meter online' },
          { name: CreditEntity.MeterOffline, label: 'Meter offline' },
        ].map(({ name, label }) => (
          <Form.Item key={name} label={label} rules={[{ min: 0, type: 'string' }]}>
            <Space>
              <Form.Item noStyle initialValue={0} name={['credit', name, 'count']} rules={numberInputRules}>
                <InputNumber controls={false} min={0} placeholder="50" style={{ width: 90 }} type="number" />
              </Form.Item>
              <Form.Item noStyle initialValue={0} name={['credit', name, 'price']} rules={numberInputRules}>
                <InputNumber
                  addonAfter={CURRENCY_TRANSLATED[currency]}
                  controls={false}
                  min={0}
                  placeholder="Cost"
                  type="number"
                />
              </Form.Item>
            </Space>
          </Form.Item>
        ))}
      </Space>
    </Form>
  );
};
